/**
 * Confidence Parsing Utility
 *
 * Parses confidence levels from AI response text using natural language patterns.
 * Maps to standardized thresholds:
 *   HIGH:   ≥90% - Direct clause match or deterministic calculation
 *   MEDIUM: 70-89% - Interpretation required, verification recommended
 *   LOW:    <70% - Insufficient or incomplete information
 *   REFUSE: Explicit - Cannot answer without missing inputs
 */

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'refuse';

export interface ConfidenceResult {
  level: ConfidenceLevel;
  percentage: number;
  reason?: string;
  requiresReview: boolean;
}

// Patterns for REFUSE detection (highest priority)
const REFUSE_PATTERNS = [
  /i cannot (?:answer|calculate|determine|provide|verify)/i,
  /i don't have (?:enough |sufficient )?(?:information|data|details)/i,
  /i'm unable to (?:answer|calculate|determine|provide)/i,
  /missing (?:the |required |necessary )?(?:information|data|input|details)/i,
  /need (?:more |additional )?(?:information|data|details|context)/i,
  /not (?:enough |sufficient )?(?:information|data) (?:to|for)/i,
  /without (?:the |knowing |having )?(?:the )?[\w\s]+(?:,| i cannot)/i,
  /(?:cannot|can't) be (?:determined|calculated|verified) without/i,
  /insufficient (?:information|data|details)/i,
  /no (?:information|data) (?:provided|available|given)/i,
  /not provided/i,
  /please provide/i,
  /i would need/i,
];

// Patterns for HIGH confidence (≥90%)
const HIGH_CONFIDENCE_PATTERNS = [
  /(?:i am |i'm )?(?:certain|confident|sure) (?:that )?/i,
  /definitely/i,
  /certainly/i,
  /absolutely/i,
  /confirmed/i,
  /exact(?:ly)? match/i,
  /precisely/i,
  /clearly (?:shows|indicates|states)/i,
  /(?:the |this )?(?:calculation|figure|amount|value) is correct/i,
  /accurately (?:reflects|shows|matches)/i,
  /verified/i,
  /matches (?:exactly|precisely)/i,
  /no (?:discrepancy|discrepancies|issues?) (?:found|detected)/i,
  /consistent with/i,
  /in accordance with/i,
];

// Patterns for LOW confidence (<70%)
const LOW_CONFIDENCE_PATTERNS = [
  /uncertain/i,
  /unclear/i,
  /(?:i'm |i am )?not (?:sure|certain)/i,
  /might (?:be|have)/i,
  /possibly/i,
  /perhaps/i,
  /insufficient/i,
  /incomplete/i,
  /cannot (?:confirm|verify)/i,
  /difficult to (?:determine|assess|verify)/i,
  /limited (?:information|data|visibility)/i,
  /(?:some |potential )?(?:ambiguity|uncertainty)/i,
  /questionable/i,
  /unverified/i,
  /approximat(?:e|ely|ion)/i,
  /rough (?:estimate|calculation)/i,
  /(?:may|could) (?:be|have) (?:issues?|errors?|discrepanc)/i,
];

// Patterns for MEDIUM confidence (70-89%) - less certain than high
const MEDIUM_CONFIDENCE_PATTERNS = [
  /likely/i,
  /probably/i,
  /appears to/i,
  /seems to/i,
  /should be/i,
  /(?:i |it )?(?:would |could )?suggest/i,
  /(?:it )?looks like/i,
  /(?:based on |according to )(?:the |this |available )?(?:information|data)/i,
  /reasonably (?:confident|certain|sure)/i,
  /(?:generally|mostly) (?:correct|accurate)/i,
  /with (?:some |minor )?(?:caveats?|reservations?)/i,
  /(?:i )?recommend (?:verifying|checking|confirming)/i,
  /verification (?:recommended|suggested|advised)/i,
  /(?:please |you should )?(?:verify|check|confirm)/i,
  /(?:pending|subject to) (?:verification|confirmation)/i,
];

// Pattern to extract missing inputs from REFUSE responses
const MISSING_INPUT_PATTERNS = [
  /without (?:the |knowing |having )?(?:the )?([\w\s,]+?)(?:\.|,| i)/i,
  /missing (?:the |required |necessary )?([\w\s,]+?)(?:\.|,|$)/i,
  /need (?:the |more |additional )?([\w\s,]+?)(?:\.|,| to)/i,
  /no ([\w\s]+?) (?:provided|available|given)/i,
  /please provide (?:the )?([\w\s,]+?)(?:\.|,|$)/i,
  /i would need (?:the )?([\w\s,]+?)(?:\.|,| to)/i,
];

/**
 * Extract what inputs are missing from a REFUSE response
 */
function extractMissingInputs(text: string): string | undefined {
  for (const pattern of MISSING_INPUT_PATTERNS) {
    const match = text.match(pattern);
    if (match && match[1]) {
      // Clean up the extracted text
      const extracted = match[1]
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/^(the|a|an)\s+/i, '');

      if (extracted.length > 2 && extracted.length < 100) {
        return extracted;
      }
    }
  }
  return undefined;
}

/**
 * Count pattern matches in text
 */
function countPatternMatches(text: string, patterns: RegExp[]): number {
  return patterns.reduce((count, pattern) => {
    return count + (pattern.test(text) ? 1 : 0);
  }, 0);
}

/**
 * Parse confidence from AI response text
 *
 * @param text - The AI response text to analyze
 * @returns ConfidenceResult with level, percentage, and optional reason
 */
export function parseConfidence(text: string): ConfidenceResult {
  // Normalize text for analysis
  const normalizedText = text.toLowerCase();

  // 1. Check for REFUSE patterns first (highest priority)
  const refuseMatches = countPatternMatches(normalizedText, REFUSE_PATTERNS);
  if (refuseMatches > 0) {
    const missingInput = extractMissingInputs(text);
    return {
      level: 'refuse',
      percentage: 0,
      reason: missingInput ? `Missing: ${missingInput}` : 'Cannot answer without required information',
      requiresReview: true,
    };
  }

  // 2. Count matches for each confidence level
  const highMatches = countPatternMatches(normalizedText, HIGH_CONFIDENCE_PATTERNS);
  const mediumMatches = countPatternMatches(normalizedText, MEDIUM_CONFIDENCE_PATTERNS);
  const lowMatches = countPatternMatches(normalizedText, LOW_CONFIDENCE_PATTERNS);

  // 3. Determine confidence level based on pattern matches
  // Low confidence patterns take precedence over high (conservative approach)
  if (lowMatches >= 2 || (lowMatches >= 1 && highMatches === 0 && mediumMatches === 0)) {
    // Strong low confidence signals
    const percentage = Math.max(30, 65 - (lowMatches * 10));
    return {
      level: 'low',
      percentage,
      reason: 'Insufficient or incomplete information detected',
      requiresReview: true,
    };
  }

  if (highMatches >= 2 && lowMatches === 0) {
    // Strong high confidence signals with no low signals
    const percentage = Math.min(98, 90 + (highMatches * 2));
    return {
      level: 'high',
      percentage,
      requiresReview: false,
    };
  }

  if (highMatches >= 1 && lowMatches === 0 && mediumMatches <= 1) {
    // Some high confidence signals, minimal medium, no low
    return {
      level: 'high',
      percentage: 90,
      requiresReview: false,
    };
  }

  if (mediumMatches >= 1 || (highMatches >= 1 && lowMatches === 0)) {
    // Medium confidence signals or mixed high without low
    const percentage = Math.min(89, Math.max(70, 75 + (highMatches * 5) - (mediumMatches * 2)));
    return {
      level: 'medium',
      percentage,
      reason: 'Verification recommended',
      requiresReview: true,
    };
  }

  if (lowMatches >= 1) {
    // Any low confidence signal
    return {
      level: 'low',
      percentage: 55,
      reason: 'Interpretation uncertain',
      requiresReview: true,
    };
  }

  // 4. Default: If no strong signals, assume medium (conservative)
  // Most AI responses that don't explicitly state confidence should be verified
  return {
    level: 'medium',
    percentage: 75,
    reason: 'No explicit confidence indicators',
    requiresReview: true,
  };
}

/**
 * Get display properties for a confidence level
 */
export function getConfidenceDisplay(level: ConfidenceLevel): {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
} {
  switch (level) {
    case 'high':
      return {
        label: 'High',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20',
      };
    case 'medium':
      return {
        label: 'Medium',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/20',
      };
    case 'low':
      return {
        label: 'Low',
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/20',
      };
    case 'refuse':
      return {
        label: 'Cannot Answer',
        color: 'text-rose-400',
        bgColor: 'bg-rose-500/10',
        borderColor: 'border-rose-500/20',
      };
  }
}

/**
 * Map a raw percentage to a confidence level
 */
export function percentageToLevel(percentage: number): ConfidenceLevel {
  if (percentage >= 90) return 'high';
  if (percentage >= 70) return 'medium';
  return 'low';
}

/**
 * Check if a confidence result requires user review
 */
export function requiresReview(result: ConfidenceResult): boolean {
  return result.level !== 'high';
}
